import React, { useState, useEffect, useRef } from 'react';
import './SalesCard.css';

const CRM_API_URL = process.env.REACT_APP_CRM_API_URL || '/api/crm';
const PIPELINE_URL = CRM_API_URL.replace('/api/crm', '/api/pipeline');

const fmt = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '-';
  const date = new Date(d + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ICON_MAP = {
  flight: '✈️', hotel: '🏨', car: '🚗', transfer: '🚐',
  bus: '🚌', tour: '🎫', cruise: '🚢', insurance: '🛡️', other: '📦'
};

const fmtDateShort = (d) => {
  if (!d) return '';
  const date = new Date(d + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const LOSS_REASONS = [
  { key: 'budget', emoji: '💰', label: 'Preço / Orçamento', hint: 'Custo excedeu capacidade financeira' },
  { key: 'competitor', emoji: '🏁', label: 'Perdeu p/ concorrente', hint: 'Comprou em outra agência' },
  { key: 'gave_up', emoji: '🚫', label: 'Desistiu da viagem', hint: 'Mudou de planos ou imprevisto' },
  { key: 'no_response', emoji: '👻', label: 'Sem resposta', hint: 'Parou de responder' },
  { key: 'disintermediation', emoji: '🔓', label: 'Comprou direto', hint: 'Reservou por conta própria' },
  { key: 'misaligned', emoji: '🎯', label: 'Expectativa desalinhada', hint: 'Queria algo diferente do possível' },
];

function FlightDetail({ item }) {
  const d = item.details || {};
  const segments = d.segments || [];
  
  if (!segments.length) {
    return <span className="sales-item-text">{item.title}</span>;
  }

  const first = segments[0];
  const last = segments[segments.length - 1];
  const airline = d.airline || '';
  const flightNum = d.flight_number || '';
  const origin = first.origin || '';
  const dest = last.destination || '';
  const depTime = first.departure_time || '';
  const arrTime = last.arrival_time || '';

  let connectionText = '';
  if (segments.length > 1) {
    const stops = segments.slice(0, -1).map(s => s.destination).filter(Boolean);
    connectionText = stops.length === 1
      ? `Conexão ${stops[0]}`
      : `${stops.length} conexões (${stops.join(', ')})`;
  }

  let baggageText = '';
  if (d.baggage) {
    const b = d.baggage;
    if (b.checked_bags > 0) {
      baggageText = `${b.checked_bags}× despacho`;
      if (b.checked_kg_per_bag) baggageText += ` ${b.checked_kg_per_bag}kg`;
    } else if (b.checked_bags === 0) {
      baggageText = 'Só mão';
    }
  } else if (d.baggage_text) {
    baggageText = d.baggage_text;
  }

  return (
    <div className="sales-flight-detail">
      <div className="sales-flight-route">
        <span className="sales-flight-airline">{airline}{flightNum ? ` · ${flightNum}` : ''}</span>
        <span className="sales-flight-leg">
          {origin} → {dest} · {depTime}–{arrTime}
        </span>
      </div>
      {(connectionText || baggageText) && (
        <div className="sales-flight-extras">
          {connectionText && <span className="sales-flight-connection">🔄 {connectionText}</span>}
          {baggageText && <span className="sales-flight-baggage">🧳 {baggageText}</span>}
        </div>
      )}
    </div>
  );
}

const detailStyle = { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 };
const titleStyle = { fontSize: '0.85rem', fontWeight: 500, color: '#1f2937', lineHeight: 1.3 };
const subStyle = { fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.4, marginTop: '2px' };

function ItemDetail({ item }) {
  const d = item.details || {};
  const type = item.type;

  if (type === 'flight' && d.segments?.length) return <FlightDetail item={item} />;

  if (type === 'hotel') {
    const checkin = d.checkin_date || item.date;
    const checkout = d.checkout_date;
    const room = d.room_type || '';
    const meal = d.meal_plan || '';
    const nights = d.nights || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {checkin && checkout ? `${fmtDateShort(checkin)} → ${fmtDateShort(checkout)}` : fmtDateShort(checkin)}
          {nights ? ` (${nights}n)` : ''}{room ? ` · ${room}` : ''}{meal ? ` · ${meal}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'bus') {
    const depTime = d.departure_time || '';
    const arrTime = d.arrival_time || '';
    const seat = d.seat_class || '';
    const company = d.company || '';
    const duration = d.duration || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {fmtDateShort(item.date)}{depTime ? ` · ${depTime}` : ''}{arrTime ? `–${arrTime}` : ''}
          {duration ? ` (${duration})` : ''}{seat ? ` · ${seat}` : ''}{company ? ` · ${company}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'transfer') {
    const origin = d.origin_name || '';
    const dest = d.destination_name || '';
    const vehicle = d.vehicle_type || '';
    const pickup = d.pickup_time || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {fmtDateShort(item.date)}{pickup ? ` · ${pickup}` : ''}{vehicle ? ` · ${vehicle}` : ''}
          {origin && dest ? ` · ${origin} → ${dest}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'car') {
    const pickup = d.pickup_date || item.date;
    const dropoff = d.dropoff_date || '';
    const pickupLoc = d.pickup_location || '';
    const days = d.days || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {pickup && dropoff ? `${fmtDateShort(pickup)} → ${fmtDateShort(dropoff)}` : fmtDateShort(pickup)}
          {days ? ` (${days}d)` : ''}{pickupLoc ? ` · ${pickupLoc}` : ''}
        </span>
      </div>
    );
  }

  if (type === 'insurance') {
    const company = d.company || '';
    const plan = d.plan_name || '';
    const coverage = d.coverage_amount || 0;
    const currency = d.coverage_currency || 'USD';
    const start = d.start_date || item.date;
    const end = d.end_date || '';
    return (
      <div style={detailStyle}>
        <span style={titleStyle}>{item.title}</span>
        <span style={subStyle}>
          {start && end ? `${fmtDateShort(start)} → ${fmtDateShort(end)}` : ''}
          {company ? ` · ${company}` : ''}{plan ? ` · ${plan}` : ''}
          {coverage > 0 ? ` · ${currency} ${coverage.toLocaleString('pt-BR')}` : ''}
        </span>
      </div>
    );
  }

  return (
    <div style={detailStyle}>
      <span style={titleStyle}>{item.title || item.type}</span>
      {(item.date || item.description) && (
        <span style={subStyle}>
          {item.date ? fmtDateShort(item.date) : ''}
          {item.description ? ` · ${item.description}` : ''}
        </span>
      )}
    </div>
  );
}

function QuoteItems({ q }) {
  const items = q.items || [];
  const markup = parseFloat(q.markup) || 0;
  const totalPrice = parseFloat(q.total_price) || 0;
  const finalPrice = totalPrice + markup;

  return (
    <>
      {items.length > 0 && (
        <div className="sales-quote-items">
          {items.map((item, i) => (
            <div key={i} className="sales-item-row">
              <span className="sales-item-icon">{ICON_MAP[item.type] || '📦'}</span>
              <ItemDetail item={item} />
              {item.price > 0 && (
                <span className="sales-item-price">R$ {fmt(item.price)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="sales-quote-price-section">
        {markup > 0 ? (
          <>
            <div className="sales-price-line">
              <span>Base</span>
              <span>R$ {fmt(totalPrice)}</span>
            </div>
            <div className="sales-price-line sales-price-markup">
              <span>Markup</span>
              <span>R$ {fmt(markup)}</span>
            </div>
            <div className="sales-price-line sales-price-total">
              <span>Total cliente</span>
              <span>R$ {fmt(finalPrice)}</span>
            </div>
          </>
        ) : (
          <div className="sales-price-line sales-price-total">
            <span>Valor</span>
            <span>R$ {fmt(finalPrice)}</span>
          </div>
        )}
      </div>
    </>
  );
}


/**
 * SalesCard - Ficha de vendas do contato.
 * Propostas com múltiplas opções usam abas para alternar.
 */
export default function SalesCard({ contactId, contactName, onClose }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lostModal, setLostModal] = useState(null);
  const [lossReason, setLossReason] = useState('');
  const [lossDetail, setLossDetail] = useState('');
  const [activeTab, setActiveTab] = useState({});
  const cardRef = useRef(null);

  useEffect(() => {
    if (!contactId) return;
    fetchQuotes();
  }, [contactId]);

  useEffect(() => {
    const handler = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Refresh automático ao voltar à aba — garante dados sempre frescos
  useEffect(() => {
    const handler = () => { if (document.visibilityState === 'visible') fetchQuotes(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [contactId]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${PIPELINE_URL}/contacts/${contactId}/quotes`);
      if (resp.ok) setProposals(await resp.json());
    } catch (e) { console.error('Erro ao buscar cotações:', e); }
    finally { setLoading(false); }
  };

  const handleMarkWon = async (quoteId) => {
    try {
      await fetch(`${PIPELINE_URL}/quotes/${quoteId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'won' })
      });
      fetchQuotes();
    } catch (e) { console.error(e); }
  };

  const handleMarkLost = async (quoteId) => {
    if (!lossReason) return;
    try {
      await fetch(`${PIPELINE_URL}/quotes/${quoteId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          status: 'lost', 
          loss_reason: lossReason,
          reason: lossDetail || undefined
        })
      });
      setLostModal(null);
      setLossReason('');
      setLossDetail('');
      fetchQuotes();
    } catch (e) { console.error(e); }
  };

  const openQuote = (quoteId) => window.open(`/app/cotacao/${quoteId}`, '_blank');

  const statusConfig = {
    draft:     { bg: '#f3f4f6', color: '#6b7280', label: 'Rascunho' },
    published: { bg: '#fef3c7', color: '#92400e', label: 'Publicada' },
    sent:      { bg: '#dbeafe', color: '#1e40af', label: '📲 Enviada' },
    won:       { bg: '#d1fae5', color: '#065f46', label: '✓ Ganho' },
    lost:      { bg: '#fee2e2', color: '#991b1b', label: '✗ Perdido' },
    paused:    { bg: '#fef9c3', color: '#854d0e', label: '⏸ Pausada' }
  };

  const getTravelers = (q) => {
    const parts = [];
    if (q.adults) parts.push(`${q.adults} adulto${q.adults > 1 ? 's' : ''}`);
    if (q.children) parts.push(`${q.children} criança${q.children > 1 ? 's' : ''}`);
    if (q.infants) parts.push(`${q.infants} bebê${q.infants > 1 ? 's' : ''}`);
    return parts.length ? parts.join(', ') : null;
  };

  return (
    <div className="sales-card-overlay">
      <div className="sales-card" ref={cardRef}>
        <div className="sales-card-header">
          <div>
            <h3>💼 Vendas</h3>
            <p className="sales-card-contact">{contactName}</p>
          </div>
          <button className="sales-card-close" onClick={onClose}>✕</button>
        </div>

        <div className="sales-card-body">
          {loading ? (
            <div className="sales-card-loading">Carregando cotações...</div>
          ) : proposals.length === 0 ? (
            <div className="sales-card-empty">
              <span className="sales-card-empty-icon">📋</span>
              <p>Nenhuma cotação vinculada</p>
              <p className="sales-card-hint">Crie uma cotação pelo Assistente vinculando a este contato</p>
            </div>
          ) : (
            proposals.map(p => {
              const sc = statusConfig[p.pipeline_status || p.status || 'draft'];
              const isOpen = !p.pipeline_status || p.pipeline_status === 'draft' || 
                             p.pipeline_status === 'sent' || p.pipeline_status === 'published' ||
                             p.pipeline_status === 'paused';
              const travelers = getTravelers(p);
              const options = p.options || [];
              const allOptions = [p, ...options];
              allOptions.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
              const hasMultiple = allOptions.length > 1;
              const currentTab = activeTab[p.id] || 0;
              const currentOption = allOptions[currentTab] || allOptions[0];
              
              return (
                <div key={p.id} className="sales-quote-card">
                  {/* Top: Destino + Status */}
                  <div className="sales-quote-top">
                    <div className="sales-quote-dest-row" onClick={() => openQuote(currentOption.id)}>
                      <span className="sales-quote-dest">✈️ {p.destination || 'Sem destino'}</span>
                    </div>
                    <span className="sales-quote-badge" style={{ backgroundColor: sc?.bg, color: sc?.color }}>
                      {sc?.label || p.pipeline_status || p.status}
                    </span>
                  </div>

                  {/* Datas + Viajantes */}
                  <div className="sales-quote-meta">
                    {p.start_date && (
                      <span className="sales-meta-item">
                        📅 {fmtDate(p.start_date)} → {fmtDate(p.end_date)}
                      </span>
                    )}
                    {travelers && (
                      <span className="sales-meta-item">👥 {travelers}</span>
                    )}
                  </div>

                  {/* Abas de opções */}
                  {hasMultiple && (
                    <div className="sales-option-tabs">
                      {allOptions.map((opt, i) => {
                        const optPrice = parseFloat(opt.total_price || 0) + parseFloat(opt.markup || 0);
                        const isActive = currentTab === i;
                        const isMostRecent = i === allOptions.length - 1;
                        return (
                          <button
                            key={opt.id}
                            className={`sales-option-tab${isActive ? ' active' : ''}`}
                            onClick={() => setActiveTab(prev => ({ ...prev, [p.id]: i }))}
                          >
                            <span className="sales-tab-label">Opção {i + 1}</span>
                            <span className="sales-tab-price">R$ {fmt(optPrice)}</span>
                            {isMostRecent && <span className="sales-tab-recent">recente</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Itens da opção selecionada */}
                  <QuoteItems q={currentOption} />

                  {(p.loss_reason || p.closed_reason) && (
                    <div className="sales-quote-reason">
                      {p.loss_reason 
                        ? `${(LOSS_REASONS.find(r => r.key === p.loss_reason) || {}).emoji || '💬'} ${(LOSS_REASONS.find(r => r.key === p.loss_reason) || {}).label || p.closed_reason}${p.loss_detail ? ` — ${p.loss_detail}` : ''}`
                        : `💬 ${p.closed_reason}`
                      }
                    </div>
                  )}

                  {isOpen && (
                    <div className="sales-quote-actions">
                      <button className="sales-btn-won" onClick={() => handleMarkWon(p.id)}>✓ Ganho</button>
                      <button className="sales-btn-lost" onClick={() => setLostModal(p.id)}>✗ Perdido</button>
                      <button className="sales-btn-open" onClick={() => openQuote(currentOption.id)}>Abrir ↗</button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {lostModal && (
          <div className="sales-lost-modal">
            <h4>Motivo da perda</h4>
            <div className="sales-loss-reasons">
              {LOSS_REASONS.map(r => (
                <button
                  key={r.key}
                  className={`sales-loss-reason-btn${lossReason === r.key ? ' active' : ''}`}
                  onClick={() => setLossReason(r.key)}
                >
                  <span className="sales-loss-emoji">{r.emoji}</span>
                  <span className="sales-loss-label">{r.label}</span>
                  <span className="sales-loss-hint">{r.hint}</span>
                </button>
              ))}
            </div>
            {lossReason && (
              <input
                type="text"
                value={lossDetail}
                onChange={(e) => setLossDetail(e.target.value)}
                placeholder="Detalhe opcional..."
                onKeyDown={(e) => { if (e.key === 'Enter') handleMarkLost(lostModal); }}
              />
            )}
            <div className="sales-lost-btns">
              <button onClick={() => { setLostModal(null); setLossReason(''); setLossDetail(''); }}>Cancelar</button>
              <button 
                className="confirm" 
                onClick={() => handleMarkLost(lostModal)}
                disabled={!lossReason}
                style={{ opacity: lossReason ? 1 : 0.4 }}
              >Confirmar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
